import {z} from 'zod';
import type {FeatureConfigOverrides} from './internals';

const RuntimeOverrideFlagsSchema = z.record(z.string(), z.boolean());

const getBuildkiteFlags = () => {
  // Only run in CI
  if (!process.env.BUILDKITE) {
    return {};
  }

  try {
    const flagsStr = process.env['WF_BK_FEATURE_FLAGS'];
    if (!flagsStr) {
      return {};
    }

    // Parse the JSON string into an object
    const buildkiteFlags = JSON.parse(flagsStr);

    console.log('Added Buildkite feature flags to overrides: ', buildkiteFlags);
    return buildkiteFlags;
  } catch (error) {
    console.error('Failed to parse WF_BK_FEATURE_FLAGS:', error);
    return {};
  }
};

export const getRuntimeOverrideFlags = () => {
  const flagsStr = process.env['WF_RUNTIME_OVERRIDE_FLAGS'];
  if (!flagsStr) {
    return {};
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(flagsStr);
  } catch (error) {
    console.error('Failed to parse WF_RUNTIME_OVERRIDE_FLAGS:', error);
    return {};
  }

  const result = RuntimeOverrideFlagsSchema.safeParse(parsed);
  if (!result.success) {
    console.error(
      'WF_RUNTIME_OVERRIDE_FLAGS must be a JSON object mapping flag names to booleans:',
      result.error.issues
    );
    return {};
  }

  console.log(
    'Added runtime override feature flags to overrides: ',
    result.data
  );
  return result.data;
};

/**
 * Determines whether the non-prod-only override block should be applied.
 * If `APP_ENV` is defined, we use that as the source of truth. If it doesn't exist, we rely on NODE_ENV.
 *
 * Note: runtime override flags from `WF_RUNTIME_OVERRIDE_FLAGS` are merged into
 * `globalOverrides` in every environment (including production), independent of
 * this check.
 */
export const shouldApplyNonProdOverrides = () => {
  if (process.env.APP_ENV !== undefined) {
    return process.env.APP_ENV !== 'production';
  }

  return process.env.NODE_ENV !== 'production';
};

// overrides stored here will take effect globally
// in test, staging, and local development. note
// that this includes CI!
//
// id-specific overrides can still be used (e.g.
// enabling/disabling a flag for a site or user)
// and will take precedence over the global
// setting for each flag/config.
const nonProdOverrides: FeatureConfigOverrides = {
  configs: {
    'config-test': {exists: true},
  },
  flags: {
    'flag-test': true,
    'ff-work-1952-disable-site-backup-submission': false,
    'ff-work-1952-disable-site-restore-submission': false,
    // Okta admin session check (TNS-330): off outside prod so test and dev
    // admin flows don't need an Okta session. Enable on a non-prod deploy
    // with WF_RUNTIME_OVERRIDE_FLAGS.
    // TODO(TNS-330): remove this override once the gate is enabled in prod so
    // CI exercises the production path; stub the flag false in the few suites
    // that still need the legacy path.
    'ff-tns-330-okta-admin-session': false,
    ...getBuildkiteFlags(),
  },
  experiments: {
    'experiment-test': {enabled: true},
  },
  layers: {
    'layer-test': {enabled: true},
  },
};

// Applied in every environment, sourced from WF_RUNTIME_OVERRIDE_FLAGS.
const runtimeOverrides: FeatureConfigOverrides = {
  flags: getRuntimeOverrideFlags(),
};

const activeNonProdOverrides: FeatureConfigOverrides =
  shouldApplyNonProdOverrides() ? nonProdOverrides : {};

export const globalOverrides: FeatureConfigOverrides = {
  ...activeNonProdOverrides,
  flags: {
    ...activeNonProdOverrides.flags,
    ...runtimeOverrides.flags,
  },
};
